/**
 * Video Reaction Actions (Schema Restructure — DB-001a/DB-001b)
 *
 * 4 quality reactions on videos (no Heart — Heart is Feed-only):
 *   - MindBlown (mutual-exclusive quick react)
 *   - Valued / Insight / Challenge (comment-gated, opens modal)
 *
 * State shape: state.emotes.reactions[videoId] = {
 *   counts: { mindblownCount, valuedCount, insightCount, challengeCount, normalCount },
 *   userState: { activeReaction: string|null }
 * }
 */
import apiClient from '../../utils/apiConfig';

// ── Action Types ────────────────────────────────────────────
export const VideoReactionActions = {
  // Load
  LOAD_REACTIONS_REQUEST:  'LOAD_REACTIONS_REQUEST',
  LOAD_REACTIONS_SUCCESS:  'LOAD_REACTIONS_SUCCESS',
  LOAD_REACTIONS_FAILURE:  'LOAD_REACTIONS_FAILURE',
  // Toggle
  TOGGLE_REACTION_REQUEST:  'TOGGLE_REACTION_REQUEST',
  TOGGLE_REACTION_OPTIMISTIC: 'TOGGLE_REACTION_OPTIMISTIC',
  TOGGLE_REACTION_SUCCESS:  'TOGGLE_REACTION_SUCCESS',
  TOGGLE_REACTION_FAILURE:  'TOGGLE_REACTION_FAILURE',
  // Housekeeping
  CLEAR_VIDEO_REACTIONS:    'CLEAR_VIDEO_REACTIONS',    // on nav away (REQ-026)
};

// ── Backward compatibility alias ────────────────────────────
// Components that still reference VideoEmoteActions will keep working
// until they are migrated in later OPs.
export const VideoEmoteActions = VideoReactionActions;

// ── Simple action creators ──────────────────────────────────

export const loadReactionsRequest = (videoId) => ({
  type: VideoReactionActions.LOAD_REACTIONS_REQUEST,
  payload: { videoId }
});

export const loadReactionsSuccess = (videoId, counts, userState) => ({
  type: VideoReactionActions.LOAD_REACTIONS_SUCCESS,
  payload: { videoId, counts, userState }
});

export const loadReactionsFailure = (videoId, error) => ({
  type: VideoReactionActions.LOAD_REACTIONS_FAILURE,
  payload: { videoId, error }
});

export const toggleReactionOptimistic = (videoId, reactionType, counts, userState) => ({
  type: VideoReactionActions.TOGGLE_REACTION_OPTIMISTIC,
  payload: { videoId, reactionType, counts, userState }
});

export const toggleReactionSuccess = (videoId, reactionType, counts, userState) => ({
  type: VideoReactionActions.TOGGLE_REACTION_SUCCESS,
  payload: { videoId, reactionType, counts, userState }
});

export const toggleReactionFailure = (videoId, reactionType, error, previousCounts, previousUserState) => ({
  type: VideoReactionActions.TOGGLE_REACTION_FAILURE,
  payload: { videoId, reactionType, error, previousCounts, previousUserState }
});

export const clearVideoReactions = (videoId) => ({
  type: VideoReactionActions.CLEAR_VIDEO_REACTIONS,
  payload: { videoId }
});

// ── Default empty state (for optimistic math) ───────────────
const EMPTY_COUNTS = {
  mindblownCount: 0, valuedCount: 0,
  insightCount: 0, challengeCount: 0, normalCount: 0
};
const EMPTY_USER_STATE = { activeReactions: {} };

// ── Thunks ──────────────────────────────────────────────────

/**
 * loadVideoReactions(videoId)
 * GET /api/videos/:videoId/reactions → stores counts + userState
 */
export const loadVideoReactions = (videoId) => async (dispatch) => {
  if (!videoId) return null;

  dispatch(loadReactionsRequest(videoId));

  try {
    const response = await apiClient.get(`/api/videos/${videoId}/reactions`);

    if (response.data.success) {
      const { counts, userState } = response.data;
      dispatch(loadReactionsSuccess(videoId, counts, userState));
      return { counts, userState };
    }
    throw new Error(response.data.message || 'Failed to load reactions');
  } catch (error) {
    const msg = error.response?.data?.message || error.message || 'Failed to load reactions';
    dispatch(loadReactionsFailure(videoId, msg));
    return null;
  }
};

/**
 * toggleReaction(videoId, reactionType)
 * POST /api/videos/:videoId/reactions
 *
 * Heart: independent toggle.
 * MindBlown: mutual-exclusive quick react — toggle on/off independently.
 * Valued/Insight/Challenge: comment-gated — handled by opening modal from EmoteButtons.
 *
 * Optimistic update with rollback on failure.
 */
export const toggleReaction = (videoId, reactionType) => async (dispatch, getState) => {
  if (!videoId || !reactionType) return null;

  const videoData = getState().emotes.reactions?.[videoId] || {};
  const prevCounts    = { ...EMPTY_COUNTS, ...videoData.counts };
  const prevUserState = { ...EMPTY_USER_STATE, ...videoData.userState };

  // ── Optimistic update (Phase 2: independent reactions) ──
  const optCounts    = { ...prevCounts };
  const optUserState = { activeReactions: { ...prevUserState.activeReactions } };

  if (reactionType === 'mindblown') {
    // MindBlown toggles independently — doesn't affect other reactions
    if (prevUserState.activeReactions?.mindblown) {
      optCounts.mindblownCount = Math.max(0, optCounts.mindblownCount - 1);
      delete optUserState.activeReactions.mindblown;
    } else {
      optCounts.mindblownCount += 1;
      optUserState.activeReactions.mindblown = true;
    }
  }
  // Valued/Insight/Challenge don't get optimistic — they require modal confirmation

  console.log('[GLOW DEBUG] OPTIMISTIC dispatch:', { reactionType, mindblownActive: optUserState.activeReactions?.mindblown, optUserState });
  dispatch(toggleReactionOptimistic(videoId, reactionType, optCounts, optUserState));

  // ── API call ───────────────────────────────────────────
  try {
    const response = await apiClient.post(`/api/videos/${videoId}/reactions`, { reactionType });
    const data = response.data;
    console.log('[GLOW DEBUG] API response:', { success: data.success, action: data.action, mindblownActive: data.userState?.activeReactions?.mindblown, userState: data.userState });

    if (!data.success && data.action === 'blocked') {
      // Challenge permanence block — rollback
      console.log('[GLOW DEBUG] BLOCKED — rolling back to prev:', { prevMindblown: prevUserState.activeReactions?.mindblown });
      dispatch(toggleReactionFailure(videoId, reactionType, data.message, prevCounts, prevUserState));
      return { blocked: true, message: data.message };
    }

    if (data.success) {
      console.log('[GLOW DEBUG] SUCCESS dispatch:', { mindblownActive: data.userState?.activeReactions?.mindblown });
      dispatch(toggleReactionSuccess(videoId, reactionType, data.counts, data.userState));
      return data;
    }

    console.log('[GLOW DEBUG] NOT SUCCESS — throwing:', data.message);
    throw new Error(data.message || 'Failed to toggle reaction');
  } catch (error) {
    console.log('[GLOW DEBUG] CATCH — FAILURE rollback:', { status: error.response?.status, message: error.message, prevMindblown: prevUserState.activeReactions?.mindblown });
    // Handle 403 blocked responses
    if (error.response?.status === 403) {
      dispatch(toggleReactionFailure(videoId, reactionType, error.response.data.message, prevCounts, prevUserState));
      return { blocked: true, message: error.response.data.message };
    }

    const msg = error.response?.data?.message || error.message || 'Failed to toggle reaction';
    dispatch(toggleReactionFailure(videoId, reactionType, msg, prevCounts, prevUserState));
    return null;
  }
};

// ── Legacy aliases (backward compat until all components are migrated) ──
export const loadVideoEmotes = loadVideoReactions;
export const updateVideoEmote = toggleReaction;

